import React, { useState } from 'react';
import { useSelector } from 'react-redux';
// eslint-disable-next-line import/no-extraneous-dependencies
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import ScheduleIcon from '@material-ui/icons/Schedule';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import moment from 'moment';
import { postRescheduleClinicianAssignment } from '../../../../actions/areas/planner';

import useTimeline from '../../timeline/useTimeline';
import {
  startEndTimeDisplay,
  getDateForApi,
  parseDateUtc,
  extractDateOnly,
  isValidDate,
} from '../../../../utils/dates';
import globals from '../../../../globals/communityGlobals';
import KeyboardDateSelector from '../../../common/KeyboardDateTimeSelectors/KeyboardDateSelector';
import KeyboardTimeSelector from '../../../common/KeyboardDateTimeSelectors/KeyboardTimeSelector';
import { getErrorProps, combineDateTime, checkRequiredOnBlur } from '../../../../utils/keyboardSelectorFunctions';

const locale = globals.getArea('nonPatientActivities');
const errors = globals.getArea('error');

export default function RescheduleNonPatientActivity({ toggleRefresh, shouldRescheduleForTeam }) {
  const {
    activeNonPatientAssignmentForReschedule,
    setActiveNonPatientAssignmentForReschedule,
    setAlertMessage,
    resetContext,
    getAccessToken,
  } = useTimeline();

  const [state, setState] = useState({
    selectedRescheduleDate: new Date(activeNonPatientAssignmentForReschedule.visitTime.actual.startDate),
    selectedStartTime: new Date(activeNonPatientAssignmentForReschedule.visitTime.actual.startDate),
    selectedEndTime: new Date(activeNonPatientAssignmentForReschedule.visitTime.actual.endDate),
  });

  const [errorMsgs, setErrorMsgs] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [isRescheduleInProgress, setIsRescheduleInProgress] = useState(false);

  const params = {
    clinicianId: activeNonPatientAssignmentForReschedule.clinicianId,
    assignmentId: activeNonPatientAssignmentForReschedule.id,
    selectedStartDate: getDateForApi(activeNonPatientAssignmentForReschedule.totalTime.actual.startDate),
    selectedEndDate: getDateForApi(activeNonPatientAssignmentForReschedule.totalTime.actual.endDate),
    isNightMode: useSelector(statee => statee.areas.dailyPlanning.scheduler.isNightMode),
    orgUnitId: useSelector(statee => statee.areas.dailyPlanning.filters.selectedTeam),
  };

  const noValidationErrors = () => {
    return (
      isValidDate(state.selectedRescheduleDate, true) &&
      isValidDate(state.selectedStartTime, true) &&
      isValidDate(state.selectedEndTime, true) &&
      !validationErrors.selectedRescheduleDate &&
      !validationErrors.selectedStartTime &&
      !validationErrors.selectedEndTime
    );
  };

  const allDatesAndTimesAreSet = () => {
    return state.selectedStartTime && state.selectedEndTime;
  };

  const isDisabledSave = () => {
    return !allDatesAndTimesAreSet() || !noValidationErrors() || isRescheduleInProgress;
  };

  const validPostingId = () => {
    return activeNonPatientAssignmentForReschedule.postingId;
  };

  const callback = result => {
    setIsRescheduleInProgress(false);
    if (result.data.exceptions.length > 0) {
      setErrorMsgs(result.data.exceptions);
    } else {
      resetContext();
      toggleRefresh();
    }
  };

  const formattedErrorMsg = () => {
    return errorMsgs.slice().map(item => <p>{item}</p>);
  };

  const errorPlaceholder = () => {
    if (errorMsgs === null) return null;
    return (
      <span className="form-validation-error">
        <br />
        {formattedErrorMsg()}
      </span>
    );
  };

  const recheduleNonPatientAssignment = () => {
    setErrorMsgs(null);
    setIsRescheduleInProgress(true);

    const model = {
      assignGuid: activeNonPatientAssignmentForReschedule.assignGuid,
      newAssignmentTime: getDateForApi(moment(state.selectedStartTime).format(globals.dateFormats.localApiFormat)),
      newAssignmentEndTime: getDateForApi(moment(state.selectedEndTime).format(globals.dateFormats.localApiFormat)),
      postingId: validPostingId(),
      oldPostingId: validPostingId(),
      orgUnitId: params.orgUnitId,
      shouldAssignToday: parseDateUtc(activeNonPatientAssignmentForReschedule.visitTime.actual.startDate).isSame(
        parseDateUtc(globals.now),
        'day',
      ),
      clinicianId: params.clinicianId,
      shouldRescheduleForTeam,
      isNightMode: params.isNightMode,
    };
    postRescheduleClinicianAssignment(getAccessToken, model, callback, setAlertMessage);
  };

  const handleDateChange = (fieldName, date) => {
    let errorMsg = null;

    if (!date) {
      errorMsg = errors.getMsg('requiredFieldValidationMessage');
    } else {
      date = extractDateOnly(date);
    }

    setValidationErrors(prevState => ({
      ...prevState,
      [fieldName]: errorMsg,
    }));
    setState(prevState => ({ ...prevState, [fieldName]: date }));
  };
  const handleTimeChange = (fieldName, time) => {
    let errorMsg = null;

    if (!time) {
      errorMsg = errors.getMsg('requiredFieldValidationMessage');
    } else if (isValidDate(state.selectedRescheduleDate, true)) {
      time = combineDateTime(state.selectedRescheduleDate, time);
    }

    setValidationErrors(prevState => ({
      ...prevState,
      [fieldName]: errorMsg,
    }));
    setState(prevState => ({ ...prevState, [fieldName]: time }));
  };

  const renderHeader = () => {
    return (
      <div className="grey-sidePanel-header">
        <div className="sidePanel-header-subtitle">
          <Box flexGrow={1} py={1}>
            {shouldRescheduleForTeam ? (
              <Typography variant="subtitle1">Reschedule Team NPA</Typography>
            ) : (
              <Typography variant="subtitle1">Reschedule NPA</Typography>
            )}
          </Box>
        </div>
        <div className="sidePanel-header-title-wrap">
          <div className="sidePanel-header-title">
            <Typography variant="h2">{activeNonPatientAssignmentForReschedule.primaryLabel}</Typography>
            <Typography variant="h3" gutterBottom>
              {activeNonPatientAssignmentForReschedule.postcode}
            </Typography>
            <Box className="header-icon-wrapper">
              <Typography variant="h5">
                <ScheduleIcon />
                <span style={{ paddingLeft: '3px', verticalAlign: 'super' }}>
                  {startEndTimeDisplay(
                    activeNonPatientAssignmentForReschedule.visitTime.actual.startDate,
                    activeNonPatientAssignmentForReschedule.visitTime.actual.endDate,
                  )}
                </span>
              </Typography>
            </Box>
          </div>
        </div>
      </div>
    );
  };

  const mainContent = () => {
    return (
      <div className="sidePanel-content">
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <Box display="flex" flexDirection="row">
            <Box p={1} flexGrow={1} style={{ width: '35%' }}>
              <KeyboardDateSelector
                id="reschedule-date"
                required
                label="Date"
                value={state.selectedRescheduleDate}
                onChange={date => handleDateChange('selectedRescheduleDate', date)}
                onBlur={event =>
                  checkRequiredOnBlur(
                    event.target.value,
                    setValidationErrors,
                    'selectedRescheduleDate',
                    validationErrors,
                  )
                }
                errorProps={getErrorProps(validationErrors ? validationErrors.selectedRescheduleDate : null)}
                disablePast
              />
            </Box>
          </Box>
          <Box display="flex" flexDirection="row">
            <Box p={1} flexGrow={1} style={{ width: '35%' }}>
              <KeyboardTimeSelector
                id="start-time"
                required
                label={`${locale.getMsg('startTime')}`}
                value={state.selectedStartTime}
                onChange={time => handleTimeChange('selectedStartTime', time)}
                onBlur={event =>
                  checkRequiredOnBlur(event.target.value, setValidationErrors, 'selectedStartTime', validationErrors)
                }
                errorProps={getErrorProps(validationErrors ? validationErrors.selectedStartTime : null)}
              />
            </Box>
            <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
              <KeyboardTimeSelector
                id="end-time"
                required
                label={`${locale.getMsg('endTime')}`}
                value={state.selectedEndTime}
                onChange={time => handleTimeChange('selectedEndTime', time)}
                onBlur={event =>
                  checkRequiredOnBlur(event.target.value, setValidationErrors, 'selectedEndTime', validationErrors)
                }
                errorProps={getErrorProps(validationErrors ? validationErrors.selectedEndTime : null)}
              />
            </Box>
          </Box>
        </MuiPickersUtilsProvider>
      </div>
    );
  };

  const actionButtons = () => {
    return (
      <div className="sidePanel-footer">
        <div className="sidePanel-buttons">
          <div className="sidePanel-BackButton">
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setActiveNonPatientAssignmentForReschedule(null);
              }}
            >
              Back
            </Button>
          </div>
          <div className="sidePanel-saveButton">
            <Button
              variant="contained"
              color="primary"
              disabled={isDisabledSave()}
              onClick={recheduleNonPatientAssignment}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="side-panel-content-wrapper">
      <SwipeableDrawer
        anchor="right"
        open={activeNonPatientAssignmentForReschedule !== null}
        onOpen={() => {}}
        onClose={() => {}}
        ModalProps={{
          onBackdropClick: () => setActiveNonPatientAssignmentForReschedule(null),
        }}
      >
        <div className="sidePanel-wrapper" style={{ width: '350px' }} id="edit-npa-side-panel">
          {renderHeader()}
          {mainContent()}
          {errorPlaceholder()}
          {actionButtons()}
        </div>
      </SwipeableDrawer>
    </section>
  );
}
